// src/contexts/AuthContext.js

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { BASE_URL } from "../config/api";

// Create the Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Stores user details: { id, username, role, permissions }
  const [token, setToken] = useState(localStorage.getItem("authToken")); // JWT token
  const [loadingAuth, setLoadingAuth] = useState(true); // Loading state for initial auth check
  const [settings, setSettings] = useState(JSON.parse(localStorage.getItem('settings'))); // <-- Load settings from storage
  const fetchAndSetSettings = useCallback(async () => {
    const currentToken = localStorage.getItem("authToken"); // Use token directly from storage or state
    if (!currentToken) return;
    try {
      // Replace api.get with a direct fetch call
      const response = await fetch(`${BASE_URL}/settings/general`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const result = await response.json();
      if (result.data) {
        setSettings(result.data);
        localStorage.setItem('settings', JSON.stringify(result.data));
      }
    } catch (error) {
      console.error("Failed to fetch general settings:", error);
      const defaultSettings = { currencySymbol: '$', currencyCode: 'USD' };
      setSettings(defaultSettings);
      localStorage.setItem('settings', JSON.stringify(defaultSettings));
    }
  }, []); // <-- Remove api and token from dependencies

  // Debugging: Log changes to user and token state
  useEffect(() => {
    console.log(
      "AuthContext: User state changed. Current user:",
      user ? user.username : "null"
    );
  }, [user]);

  useEffect(() => {
    console.log(
      "AuthContext: Token state changed. Current token status:",
      token ? "exists" : "null"
    );
  }, [token]);

    useEffect(() => {
    // Fetch settings on initial load if token exists but settings don't
    if (token && !settings) {
      fetchAndSetSettings();
    }
  }, [token, settings, fetchAndSetSettings]);

  // Effect to perform initial authentication check from localStorage
  useEffect(() => {
    const loadAuthData = async () => {
      const storedToken = localStorage.getItem("authToken");
      if (storedToken) {
        try {
          // IMPORTANT: In a real app, validate token with backend and fetch user details securely.
          // This is a dummy decode for demonstration.
          const dummyPayload = JSON.parse(atob(storedToken.split(".")[1]));

          const userResponse = await fetch(`${BASE_URL}/profile`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });


          if (userResponse.ok) {
            const { user: userData } = await userResponse.json();
          console.log("AuthContext: Validating token and fetching user data...",userData.profilePic);

            setUser({
              id: userData._id,
              name: userData.name,
              username: userData.username,
              role: userData.role,
              permissions: userData?.permissions || [],
              organizationId: userData.organizationId,
              profilePicture: userData.profilePic,
              email: userData.email,
              phone: userData.phone,
              subscription: userData.subscription,
            });
            setToken(storedToken);
            console.log("AuthContext: Initial auth check successful.");
          } else {
            console.error(
              "AuthContext: Failed to validate token or fetch user data on init:",
              userResponse.statusText
            );
            setToken(null);
            localStorage.removeItem("authToken");
          }
        } catch (e) {
          console.error(
            "AuthContext: Error loading auth data from storage:",
            e
          );
          setToken(null);
          localStorage.removeItem("authToken");
        }
      }
      setLoadingAuth(false);
    };

    loadAuthData();
  }, []); // Run only once on component mount

  // Login function
  const login = useCallback(async (username, password) => {
    setLoadingAuth(true);
    try {
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        const receivedToken = data.token;
        const userData = data.user;

        setToken(receivedToken);
        setUser({
          ...userData,
          id: userData._id,
          subscription: userData.subscription
        });
        localStorage.setItem("authToken", receivedToken);
        await fetchAndSetSettings(); // Fetch settings immediately after login
        console.log("AuthContext: Login successful.");
        return true;
      } else {
        throw new Error(data.message || "Login failed");
      }
    } catch (error) {
      console.error("AuthContext: Login error:", error);
      setToken(null);
      setUser(null);
      localStorage.removeItem("authToken");
      throw error;
    } finally {
      setLoadingAuth(false);
    }
  }, []); // login is stable unless these dependencies change, which they shouldn't in this case

  // Logout function
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("authToken");
    console.log("AuthContext: Logged out.");
  }, []); // logout is stable

  // Permission check function
  const hasPermission = useCallback(
    (permission) => {
      return user?.permissions?.includes(permission) || false;
    },
    [user]
  ); // hasPermission is stable as long as 'user' is stable

  // --- NEW FUNCTION ---
  // Add the forgotPassword function here
  const forgotPassword = async (email) => {
    return fetch(`${BASE_URL}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  };

  const updateUserProfile = async (formData) => {
    try {
      console.log("AuthContext: Updating user profile...",formData);
      const response = await fetch(`${BASE_URL}/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // Include the token for authentication
        },
        body: JSON.stringify(formData), // FormData object for multipart/form-data
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update profile.");
      }

      const updatedUser = await response.json();
      setUser((prevUser) => ({
        ...prevUser,
        ...updatedUser, // Merge updated fields into the existing user object
      }));

      console.log("AuthContext: Profile updated successfully.");
      return updatedUser;
    } catch (error) {
      console.error("AuthContext: Error updating profile:", error);
      throw error;
    }
  };

  const resetPassword = async (token, password, confirmPassword) => {
    const response = await fetch(`${BASE_URL}/auth/reset-password/${token}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password, confirmPassword }),
    });

    // This is the critical part. If the API response is not successful (e.g., status 400, 404, 500),
    // response.ok will be false.
    if (!response.ok) {
      // We must parse the JSON body to get the specific error message from the backend.
      const errorData = await response.json();
      // Then, we THROW an error. This stops the function and triggers the .catch() block in the page component.
      throw new Error(errorData.message || 'Failed to reset password. The link may be invalid or expired.');
    }

    // This line is only reached if the API call was successful.
    return response.json();
  };

  // The context value provided to children
  const authContextValue = {
    user,
    token,
    settings, // <-- Ensure this line exists
    loadingAuth,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    hasPermission,
    forgotPassword,
    resetPassword,
    updateUserProfile,
  };

  // Debugging: Log when the context value object itself is re-created
  const prevAuthContextValueRef = React.useRef(authContextValue);
  React.useEffect(() => {
    if (prevAuthContextValueRef.current !== authContextValue) {
      console.log(
        "AuthContext: The authContextValue object reference changed!"
      );
      prevAuthContextValueRef.current = authContextValue;
    }
  }, [authContextValue]);

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the Auth Context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Export AuthContext itself, AuthProvider, and useAuth hook
export { AuthContext, AuthProvider, useAuth };
